import { ref } from 'vue'
import { supabase } from '@/lib/supabase'
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js'

/**
 * Composable pour gérer les subscriptions Supabase Realtime
 * Permet de synchroniser les données en temps réel entre différents appareils
 * 
 * Note: Utilisé dans les stores Pinia, donc pas besoin de onUnmounted
 */
export function useRealtimeSync() {
    const channels = ref<Map<string, any>>(new Map())
    const isSubscribed = ref(false)

    /**
     * S'abonner aux changements d'une table Supabase
     * @param channelName - Nom unique du channel
     * @param table - Nom de la table à écouter
     * @param callbacks - Fonctions de callback pour chaque type d'événement
     * @param filter - Filtre optionnel (ex: household_id=eq.123)
     */
    function subscribeToTable<T = any>(
        channelName: string,
        table: string,
        callbacks: {
            onInsert?: (payload: RealtimePostgresChangesPayload<T>) => void
            onUpdate?: (payload: RealtimePostgresChangesPayload<T>) => void
            onDelete?: (payload: RealtimePostgresChangesPayload<T>) => void
        },
        filter?: string
    ) {
        // Éviter les doublons de subscriptions
        if (channels.value.has(channelName)) return

        if (import.meta.env?.DEV) console.debug(`[Realtime] Subscribing to ${table} (${channelName})`)

        const channel = supabase.channel(channelName)

        // Configuration de l'événement INSERT
        if (callbacks.onInsert) {
            channel.on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table,
                    ...(filter && { filter })
                },
                (payload) => {
                    try {
                        callbacks.onInsert!(payload as RealtimePostgresChangesPayload<T>)
                    } catch (error) {
                        console.error(`[Realtime] Error in onInsert callback for ${table}:`, error)
                    }
                }
            )
        }

        // Configuration de l'événement UPDATE
        if (callbacks.onUpdate) {
            channel.on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table,
                    ...(filter && { filter })
                },
                (payload) => {
                    try {
                        callbacks.onUpdate!(payload as RealtimePostgresChangesPayload<T>)
                    } catch (error) {
                        console.error(`[Realtime] Error in onUpdate callback for ${table}:`, error)
                    }
                }
            )
        }

        // Configuration de l'événement DELETE
        if (callbacks.onDelete) {
            channel.on(
                'postgres_changes',
                {
                    event: 'DELETE',
                    schema: 'public',
                    table,
                    ...(filter && { filter })
                },
                (payload) => {
                    try {
                        callbacks.onDelete!(payload as RealtimePostgresChangesPayload<T>)
                    } catch (error) {
                        console.error(`[Realtime] Error in onDelete callback for ${table}:`, error)
                    }
                }
            )
        }

        // S'abonner au channel
        channel.subscribe((status) => {
            if (status === 'SUBSCRIBED') {
                isSubscribed.value = true
            }
        })

        // Stocker le channel pour pouvoir le nettoyer plus tard
        channels.value.set(channelName, channel)
    }

    /**
     * Se désabonner d'un channel spécifique
     */
    async function unsubscribeFromChannel(channelName: string) {
        const channel = channels.value.get(channelName)
        if (channel) {
            console.log(`[Realtime] Unsubscribing from channel: ${channelName}`)
            await supabase.removeChannel(channel)
            channels.value.delete(channelName)
        }
    }

    /**
     * Se désabonner de tous les channels
     */
    async function unsubscribeAll() {
        console.log(`[Realtime] Unsubscribing from all channels (${channels.value.size} active)`)

        for (const channel of channels.value.values()) {
            await supabase.removeChannel(channel)
        }

        channels.value.clear()
        isSubscribed.value = false
    }

    return {
        subscribeToTable,
        unsubscribeFromChannel,
        unsubscribeAll,
        isSubscribed,
        activeChannelsCount: () => channels.value.size
    }
}

